import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  FiCalendar,
  FiMail,
  FiLogOut,
  FiShield,
  FiBriefcase,
  FiServer,
  FiClipboard,
  FiBook,
  FiRefreshCw,
} from 'react-icons/fi';
import { API_BASE } from '../lib/apiBase';

/**
 * AdminDashboard now supports two roles: admin and office. The office account has
 * restricted access to key tools (events, inquiries, interests, job publishing),
 * while the admin account can also review pending approvals, services used, and
 * the change log.
 */
const MANUAL_PATH = '/documents/Avenue_School_User_Manual.pdf';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(() => {
    const storedRole = sessionStorage.getItem('userRole');
    if (storedRole) return storedRole;
    return sessionStorage.getItem('isAdmin') === '1' ? 'admin' : null;
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [maintenanceUpdating, setMaintenanceUpdating] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState('');

  useEffect(() => {
    let ignore = false;

    const loadMaintenance = async () => {
      try {
        const res = await fetch(`${API_BASE}/meta/maintenance`);
        if (!res.ok) throw new Error('Failed to fetch maintenance status');
        const data = await res.json();
        if (!ignore) {
          setMaintenanceEnabled(Boolean(data?.enabled));
          setMaintenanceError('');
        }
      } catch (err) {
        console.error('Failed to load maintenance status', err);
        if (!ignore) {
          setMaintenanceError('Unable to load maintenance status.');
        }
      } finally {
        if (!ignore) {
          setMaintenanceLoading(false);
        }
      }
    };

    loadMaintenance();

    return () => {
      ignore = true;
    };
  }, []);

  const resetSession = () => {
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('isAdmin');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const accountType = selectedAccount || 'admin';
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: accountType, password: passwordInput }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data?.message || 'Login failed.');
        setLoading(false);
        return;
      }
      sessionStorage.setItem('userRole', data.role);
      if (data.role === 'admin') {
        sessionStorage.setItem('isAdmin', '1');
      } else {
        sessionStorage.removeItem('isAdmin');
      }
      setSelectedAccount(accountType);
      setUserRole(data.role);
      setPasswordInput('');
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to log in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setUserRole(null);
    setPasswordInput('');
    resetSession();
    setSelectedAccount(null);
    navigate('/a7dash87');
  };

  const handleViewManual = async () => {
    try {
      const res = await fetch(MANUAL_PATH, { method: 'HEAD' });
      if (!res.ok) throw new Error('Manual missing');
      setManualModalOpen(true);
    } catch (err) {
      console.error('Manual viewer fallback triggered:', err);
      window.open(MANUAL_PATH, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCloseManual = () => setManualModalOpen(false);

  const handleMaintenanceToggle = async () => {
    if (!isAdmin || maintenanceLoading || maintenanceUpdating) return;

    const next = !maintenanceEnabled;
    setMaintenanceUpdating(true);
    setMaintenanceError('');

    try {
      const res = await fetch(`${API_BASE}/meta/maintenance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });

      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      const updated = Boolean(data?.enabled);
      setMaintenanceEnabled(updated);
      window.dispatchEvent(
        new CustomEvent('maintenance-mode-changed', {
          detail: { enabled: updated },
        })
      );
    } catch (err) {
      console.error('Failed to update maintenance status', err);
      setMaintenanceError('Unable to update maintenance status. Please try again.');
    } finally {
      setMaintenanceUpdating(false);
    }
  };

  if (!userRole && !selectedAccount) {
    return (
      <Container className="py-5">
        <Row className="g-4 justify-content-center">
          <Col md={5} lg={4}>
            <Card className="shadow-sm border-0 h-100" role="button" onClick={() => setSelectedAccount('admin')}>
              <Card.Body className="p-4 text-center d-flex flex-column gap-3 justify-content-center">
                <div className="d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary rounded-circle" style={{ width: 72, height: 72 }}>
                  <FiShield size={28} />
                </div>
                <div>
                  <h4 className="mb-1">Admin Login</h4>
                  <p className="text-muted mb-0">Full access to approvals, services, and settings.</p>
                </div>
                <div className="mt-2">
                  <Button variant="primary">Continue as Admin</Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={5} lg={4}>
            <Card className="shadow-sm border-0 h-100" role="button" onClick={() => setSelectedAccount('office')}>
              <Card.Body className="p-4 text-center d-flex flex-column gap-3 justify-content-center">
                <div className="d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning rounded-circle" style={{ width: 72, height: 72 }}>
                  <FiBriefcase size={28} />
                </div>
                <div>
                  <h4 className="mb-1">Office Login</h4>
                  <p className="text-muted mb-0">Manage events, inquiries, interests, and jobs.</p>
                </div>
                <div className="mt-2">
                  <Button variant="warning">Continue as Office</Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!userRole && selectedAccount) {
    const greetings = {
      admin: {
        heading: 'Hello Jarrid!',
        subheading: 'Please enter your admin password to continue.',
        iconBg: 'bg-primary-subtle text-primary',
        icon: <FiShield size={28} />,
      },
      office: {
        heading: 'Hello staff member!',
        subheading: 'Enter your office password to access the dashboard.',
        iconBg: 'bg-warning-subtle text-warning',
        icon: <FiBriefcase size={28} />,
      },
    };

    const copy = greetings[selectedAccount] ?? greetings.admin;

    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <div className={`d-inline-flex align-items-center justify-content-center rounded-circle mb-3 ${copy.iconBg}`} style={{ width: 72, height: 72 }}>
                    {copy.icon}
                  </div>
                  <h4 className="mb-1">{copy.heading}</h4>
                  <p className="text-muted mb-0">{copy.subheading}</p>
                </div>
                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder={`Enter ${selectedAccount} password`}
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      required
                    />
                  </Form.Group>
                  {error && (
                    <Alert variant="danger">{error}</Alert>
                  )}
                  <div className="d-grid">
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading ? 'Logging in…' : 'Login'}
                    </Button>
                  </div>
                </Form>
                <Button variant="link" className="mt-3 p-0" onClick={() => { setSelectedAccount(null); setPasswordInput(''); setError(''); }}>
                  Choose a different account
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  const isAdmin = userRole === 'admin';

  const handleNavigate = (path) => () => navigate(path);

  const officeCards = [
    {
      icon: <FiCalendar size={22} className="text-primary" />,
      title: 'Event Management',
      subtitle: 'Create or review school events',
      bg: 'bg-primary-subtle',
      actions: [
        { label: 'Create Event', variant: 'primary', onClick: handleNavigate('/a7dash87/create-event') },
        { label: 'Manage Events', variant: 'outline-primary', onClick: handleNavigate('/a7dash87/events') },
      ],
    },
    {
      icon: <FiBriefcase size={22} className="text-success" />,
      title: 'Staff Services',
      subtitle: 'Review interests and manage job listings',
      bg: 'bg-success-subtle',
      anchorActions: true,
      actions: [
        { label: 'View Interests', variant: 'success', onClick: handleNavigate('/a7dash87/interests') },
        { label: 'Open a Position', variant: 'success', onClick: handleNavigate('/a7dash87/publish-jobs') },
        { label: 'Manage Jobs', variant: 'outline-success', onClick: handleNavigate('/a7dash87/manage-jobs') },
        { label: 'View Applications', variant: 'outline-success', onClick: handleNavigate('/a7dash87/job-applications') },
      ],
    },
    {
      icon: <FiMail size={22} className="text-info" />,
      title: 'View Inquiries',
      subtitle: 'Manage enrolments & messages',
      bg: 'bg-info-subtle',
      actions: [
        { label: 'Go to Inquiries', variant: 'info', onClick: handleNavigate('/a7dash87/inquiries') },
      ],
    },
  ];

  const adminCards = [
    {
      icon: <FiClipboard size={22} className="text-danger" />,
      title: 'Awaiting Approval',
      subtitle: 'Review pending events & jobs',
      bg: 'bg-danger-subtle',
      actions: [
        { label: 'Review Queue', variant: 'danger', onClick: handleNavigate('/a7dash87/approvals') },
      ],
    },
  ];

  const contactItCard = {
    icon: <FiMail size={22} className="text-info" />,
    title: 'Contact IT',
    subtitle: 'Reach out to the technical support team',
    bg: 'bg-info-subtle',
    actions: [
      { label: 'Get Support', variant: 'outline-info', onClick: handleNavigate('/a7dash87/contact-it') },
    ],
  };

  const websiteStatusCard = {
    icon: <FiRefreshCw size={22} className="text-warning" />,
    title: 'Website Status',
    subtitle: 'Switch the public site into maintenance mode',
    bg: 'bg-warning-subtle',
    renderFooter: () => (
      <div className="d-flex flex-column gap-2">
        <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
          <div>
            <div className="fw-semibold">
              {maintenanceEnabled ? 'Maintenance mode enabled' : 'Site is live'}
            </div>
            <small className="text-muted">
              {maintenanceEnabled
                ? 'Visitors see the maintenance page until you switch this off.'
                : 'Visitors can access all public pages.'}
            </small>
          </div>
          <div className="ms-md-auto">
            <Form.Check
              type="switch"
              id="maintenance-mode-toggle"
              label={maintenanceEnabled ? 'On' : 'Off'}
              checked={maintenanceEnabled}
              disabled={!isAdmin || maintenanceLoading || maintenanceUpdating}
              onChange={handleMaintenanceToggle}
            />
          </div>
        </div>
        {(maintenanceLoading || maintenanceUpdating) && (
          <div className="d-flex align-items-center text-muted gap-2">
            <Spinner animation="border" size="sm" role="status" />
            <small>{maintenanceLoading ? 'Loading status…' : 'Applying update…'}</small>
          </div>
        )}
        {!!maintenanceError && (
          <small className="text-danger">{maintenanceError}</small>
        )}
        {!isAdmin && (
          <small className="text-muted">Only administrators can change this setting.</small>
        )}
      </div>
    ),
  };

  const userManualCard = {
    icon: <FiBook size={22} className="text-success" />,
    title: 'User Manual',
    subtitle: 'Guidance for day-to-day operations',
    bg: 'bg-success-subtle',
    actions: [
      { label: 'View', variant: 'success', onClick: handleViewManual },
      {
        label: 'Download',
        variant: 'outline-success',
        href: MANUAL_PATH,
        download: 'Avenue_School_User_Manual.pdf',
        rel: 'noopener noreferrer',
      },
    ],
  };

  const servicesUsedCard = {
    icon: <FiServer size={22} className="text-secondary" />,
    title: 'Services Used',
    subtitle: 'Hosting & tooling overview',
    bg: 'bg-secondary-subtle',
    actions: [
      { label: 'View Services', variant: 'secondary', onClick: handleNavigate('/a7dash87/services-used') },
    ],
  };

  const technicalCards = isAdmin
    ? [contactItCard, websiteStatusCard, userManualCard, servicesUsedCard]
    : [contactItCard];

const SECTION_DIVIDER_STYLE = { borderTop: '1px solid rgba(0,0,0,0.08)' };

const Section = ({ title, description, cards, showDivider = true }) => (
  <section
    className={`mb-5 ${showDivider ? 'pt-4 mt-4' : ''}`}
    style={showDivider ? SECTION_DIVIDER_STYLE : undefined}
  >
    <div className="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h4 className="mb-1">{title}</h4>
        <div className="text-muted">{description}</div>
      </div>
    </div>
      <Row className="g-4">
        {cards.map((card, index) => (
          <Col md={6} lg={4} key={`${title}-${index}`}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="p-4 d-flex flex-column gap-3">
                <div className="d-flex align-items-center mb-3">
                  <div
                    className={`rounded-circle ${card.bg} d-flex align-items-center justify-content-center me-3`}
                    style={{ width: 48, height: 48 }}
                  >
                    {card.icon}
                  </div>
                  <div>
                    <h5 className="mb-0">{card.title}</h5>
                    <small className="text-muted">{card.subtitle}</small>
                  </div>
                </div>
                <div className={`d-flex flex-column gap-3 ${card.anchorActions ? 'mt-auto' : ''}`}>
                  {typeof card.renderFooter === 'function' && (
                    <div>{card.renderFooter()}</div>
                  )}
                  {card.extraContent && !card.renderFooter && (
                    <div>{card.extraContent}</div>
                  )}
                  {Array.isArray(card.actions) && card.actions.length > 0 && (
                    <div className="d-grid gap-2">
                      {card.actions.map((action, actionIndex) => {
                        if (action.href) {
                          return (
                            <Button
                              key={actionIndex}
                              as="a"
                              href={action.href}
                              target={action.target}
                              rel={action.rel}
                              download={action.download}
                              variant={action.variant}
                            >
                              {action.label}
                            </Button>
                          );
                        }

                        return (
                          <Button key={actionIndex} variant={action.variant} onClick={action.onClick}>
                            {action.label}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </section>
  );

  return (
    <Container className="py-5">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
        <div>
          <h2 className="mb-1">{isAdmin ? 'Admin Dashboard' : 'Office Dashboard'}</h2>
          <div className="text-muted">
            {isAdmin
              ? 'Manage all aspects of the school website, approve pending changes, and view platform health.'
              : 'Create content and manage submissions. Pending changes require admin approval.'}
          </div>
        </div>
        <Button variant="outline-secondary" onClick={handleLogout} className="mt-3 mt-md-0">
          <FiLogOut className="me-2" /> Logout
        </Button>
      </div>

      {isAdmin && (
        <Section
          title="Admin Use"
          description="Review and approve submissions from the office team."
          cards={adminCards}
          showDivider={false}
        />
      )}

      <Section
        title="Office Use"
        description="Day-to-day tools for the office team to manage content."
        cards={officeCards}
        showDivider={isAdmin}
      />

      <Section
        title="Technical"
        description="Infrastructure and support information for the digital team."
        cards={technicalCards}
      />

      <Modal
        show={manualModalOpen}
        onHide={handleCloseManual}
        size="xl"
        centered
        aria-labelledby="manual-viewer-title"
        dialogClassName="pdf-viewer-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="manual-viewer-title">Avenue School User Manual</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <iframe
            title="Avenue School User Manual"
            src={MANUAL_PATH}
            style={{ width: '100%', height: '80vh', border: 'none' }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseManual}>
            Close
          </Button>
          <Button as="a" variant="success" href={MANUAL_PATH} download="Avenue_School_User_Manual.pdf">
            Download Manual
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
